import { Clock, Copy, ImageIcon, Plus, Store, Trash2, Truck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ImageDropZone } from "@/components/ImageUpload";

const INPUT = "mt-1 h-11 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-base outline-none transition-colors focus:border-slate-900 sm:text-sm";

function Field({ label, k, f, setF, className = "", ...p }) {
  return (
    <label className={`block min-w-0 ${className}`}><span className="text-xs font-semibold text-slate-600">{label}</span>
      <input value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`store-field-${k}`} className={INPUT} {...p} /></label>
  );
}

export function Section({ id, title, icon: Icon, hint, children }) {
  return (
    <section id={`store-${id}`} className="scroll-mt-32 rounded-2xl border border-slate-200 bg-white p-4 sm:p-6" data-testid={`store-section-${id}`}>
      <div className="mb-4 flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-900 text-amber-400"><Icon className="h-4 w-4" /></span>
        <div className="min-w-0"><h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

const Toggle = ({ k, label, f, setF }) => (
  <label className="flex min-h-[44px] items-center justify-between gap-3 text-sm font-semibold">{label}<Switch checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: v })} data-testid={`store-toggle-${k}`} /></label>
);

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
export const defaultWeek = () => Object.fromEntries(DAYS.map(([d]) => [d, { closed: false, open: "09:00", close: "21:00" }]));
const browserTz = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } };

export const emptyStore = (slug, branchId) => ({ slug, name: "", about: "", phone: "", whatsapp: "", hours: "", is_open: true, branch_id: branchId, pickup_address: "",
  delivery_enabled: true, pickup_enabled: true, min_order: "0", areas: [], logo_url: "", banner_url: "", week: defaultWeek(), timezone: browserTz() });

export function DetailsSection({ f, setF, branches }) {
  return (
    <Section id="details" title="Store details" icon={Store} hint="Your store name, web address and how customers reach you.">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Store name" k="name" f={f} setF={setF} />
        <Field label="Store address (slug)" k="slug" f={f} setF={setF} autoCapitalize="none" />
      </div>
      <label className="block"><span className="text-xs font-semibold text-slate-600">About</span>
        <textarea value={f.about || ""} onChange={(e) => setF({ ...f, about: e.target.value })} rows={3} data-testid="store-field-about" className="mt-1 w-full rounded-xl border border-slate-200 p-3 text-base outline-none focus:border-slate-900 sm:text-sm" /></label>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Phone" k="phone" f={f} setF={setF} inputMode="tel" />
        <Field label="WhatsApp" k="whatsapp" f={f} setF={setF} inputMode="tel" />
        <label className="block min-w-0"><span className="text-xs font-semibold text-slate-600">Fulfilment branch</span>
          <select value={f.branch_id || ""} onChange={(e) => setF({ ...f, branch_id: e.target.value })} data-testid="store-field-branch" className={INPUT}>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select></label>
        <Field label="Pickup address" k="pickup_address" f={f} setF={setF} />
      </div>
    </Section>
  );
}

export function BrandingSection({ f, setF, client, uploadPath, uploadParams }) {
  return (
    <Section id="branding" title="Branding" icon={ImageIcon} hint="JPG, PNG or WebP up to 5 MB. Resized on your device.">
      <div className="grid gap-4 md:grid-cols-[180px_minmax(0,1fr)] [&>*:first-child]:max-w-[180px]">
        <ImageDropZone label="Logo (square)" ratio="aspect-square" value={f.logo_url} onChange={(u) => setF({ ...f, logo_url: u })} client={client} path={uploadPath} params={uploadParams} testid="store-logo" />
        <ImageDropZone label="Banner (wide, 3:1)" ratio="aspect-[3/1]" value={f.banner_url} onChange={(u) => setF({ ...f, banner_url: u })} client={client} path={uploadPath} params={uploadParams} testid="store-banner" />
      </div>
    </Section>
  );
}

export function HoursSection({ f, setF }) {
  const week = f.week;
  const setDay = (d, k, v) => setF({ ...f, week: { ...week, [d]: { ...week[d], [k]: v } } });
  const copyMonday = () => setF({ ...f, week: Object.fromEntries(DAYS.map(([d]) => [d, { ...week.mon }])) });
  return (
    <Section id="hours" title="Opening hours" icon={Clock} hint="Outside these hours customers see when you open next.">
      <Toggle k="is_open" label="Open for orders (overrides the schedule)" f={f} setF={setF} />
      <Field label="Hours note shown on the store" k="hours" f={f} setF={setF} placeholder="Daily 9:00–22:00" />
      <div className="rounded-xl border border-slate-200" data-testid="store-weekly-hours">
        <label className="flex min-h-[52px] items-center justify-between gap-3 border-b border-slate-100 px-3 text-sm font-semibold">Weekly schedule
          <Switch checked={!!week} onCheckedChange={(v) => setF({ ...f, week: v ? defaultWeek() : null, timezone: f.timezone || browserTz() })} data-testid="store-week-toggle" /></label>
        {week && (
          <div className="divide-y divide-slate-100">
            {DAYS.map(([d, label]) => (
              <div key={d} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-3 py-2.5 sm:grid-cols-[110px_96px_minmax(0,1fr)]" data-testid={`store-hours-${d}`}>
                <span className="text-sm font-medium">{label}</span>
                <label className="flex items-center justify-end gap-2 text-xs text-slate-500 sm:justify-start"><Switch checked={!week[d].closed} onCheckedChange={(v) => setDay(d, "closed", !v)} data-testid={`store-hours-open-${d}`} />{week[d].closed ? "Closed" : "Open"}</label>
                {!week[d].closed ? (
                  <div className="col-span-2 flex min-w-0 items-center gap-2 sm:col-span-1">
                    <input type="time" value={week[d].open} onChange={(e) => setDay(d, "open", e.target.value)} data-testid={`store-hours-from-${d}`} className="h-11 min-w-0 flex-1 rounded-xl border px-2 text-sm" />
                    <span className="text-xs text-slate-400">to</span>
                    <input type="time" value={week[d].close} onChange={(e) => setDay(d, "close", e.target.value)} data-testid={`store-hours-to-${d}`} className="h-11 min-w-0 flex-1 rounded-xl border px-2 text-sm" />
                  </div>) : <span className="hidden text-xs text-slate-400 sm:block">Closed all day</span>}
              </div>))}
            <div className="flex flex-wrap items-end gap-3 p-3">
              <button type="button" onClick={copyMonday} data-testid="store-hours-copy-monday" className="flex h-11 items-center gap-1.5 rounded-full border px-4 text-sm font-semibold hover:bg-slate-50"><Copy className="h-4 w-4" />Copy Monday to all days</button>
              <label className="block min-w-[180px] flex-1"><span className="text-xs font-semibold text-slate-600">Time zone</span>
                <input value={f.timezone || ""} onChange={(e) => setF({ ...f, timezone: e.target.value })} data-testid="store-field-timezone" className={INPUT} placeholder="Asia/Karachi" /></label>
            </div>
          </div>)}
      </div>
    </Section>
  );
}

export function DeliverySection({ f, setF }) {
  const area = (i, k, v) => setF({ ...f, areas: f.areas.map((a, j) => (j === i ? { ...a, [k]: v } : a)) });
  return (
    <Section id="delivery" title="Delivery & pickup" icon={Truck} hint="Cash on delivery or pay at pickup.">
      <div className="grid gap-1 md:grid-cols-2 md:gap-x-6">
        <Toggle k="delivery_enabled" label="Delivery (cash on delivery)" f={f} setF={setF} />
        <Toggle k="pickup_enabled" label="Pickup (pay at pickup)" f={f} setF={setF} />
      </div>
      <Field label="Minimum order for delivery" k="min_order" f={f} setF={setF} inputMode="decimal" className="md:max-w-xs" />
      <div className="text-xs font-semibold text-slate-600">Delivery areas & fees</div>
      <div className="grid gap-3 md:grid-cols-2">
        {(f.areas || []).map((a, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`store-area-${i}`}>
            <div className="flex items-center justify-between"><span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Area {i + 1}</span>
              <button type="button" onClick={() => setF({ ...f, areas: f.areas.filter((_, j) => j !== i) })} data-testid={`store-area-remove-${i}`} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:text-rose-600" aria-label="Remove area"><Trash2 className="h-4 w-4" /></button></div>
            <div className="grid grid-cols-[minmax(0,1fr)_112px] gap-2">
              <input value={a.name} onChange={(e) => area(i, "name", e.target.value)} placeholder="Area name" data-testid={`store-area-name-${i}`} className="h-11 min-w-0 rounded-xl border bg-white px-3 text-base sm:text-sm" />
              <input value={a.fee} onChange={(e) => area(i, "fee", e.target.value)} placeholder="Fee" inputMode="decimal" data-testid={`store-area-fee-${i}`} className="h-11 min-w-0 rounded-xl border bg-white px-3 text-base sm:text-sm" />
            </div>
          </div>))}
      </div>
      <button type="button" onClick={() => setF({ ...f, areas: [...(f.areas || []), { name: "", fee: "0" }] })} data-testid="store-area-add" className="flex h-11 items-center gap-1.5 rounded-full border border-dashed border-emerald-300 px-4 text-sm font-bold text-emerald-700"><Plus className="h-4 w-4" />Add area</button>
    </Section>
  );
}

export const storeBody = (f) => ({ ...f, logo_url: f.logo_url || null, banner_url: f.banner_url || null, areas: (f.areas || []).map((a) => ({ name: a.name, fee: a.fee })) });

export function StoreSettingsForm({ f, setF, branches, client, uploadPath, uploadParams }) {
  return (
    <div className="space-y-4">
      <DetailsSection f={f} setF={setF} branches={branches} />
      <BrandingSection f={f} setF={setF} client={client} uploadPath={uploadPath} uploadParams={uploadParams} />
      <HoursSection f={f} setF={setF} />
      <DeliverySection f={f} setF={setF} />
    </div>
  );
}
